import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { LogOut, CheckCircle, XCircle, Clock, Users, BarChart3, DollarSign, CreditCard, Plus, Loader2 } from "lucide-react";
import ClubAnalytics from "./ClubAnalytics";
import SupervisorEarningsTab from "./SupervisorEarningsTab";
import StripeConnectTab from "./StripeConnectTab";

const SupervisorDashboard = ({ user, logout }) => {
  const [hours, setHours] = useState([]);
  const [loading, setLoading] = useState({});
  const [activeTab, setActiveTab] = useState("approvals");
  const [pendingVolunteers, setPendingVolunteers] = useState([]);
  const [showLogHoursDialog, setShowLogHoursDialog] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [myHours, setMyHours] = useState([]);
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    start_time: "09:00",
    hours: "",
    activity_type: "",
    description: ""
  });

  const activityTypes = ["coaching", "maintenance", "admin", "meeting", "community work", "other"];
  
  const activityDefaults = {
    coaching: { hours: "2", start_time: "19:00" },
    maintenance: { hours: "3", start_time: "10:00" },
    admin: { hours: "1", start_time: "09:00" },
    meeting: { hours: "2", start_time: "18:00" },
    "community work": { hours: "3", start_time: "10:00" },
    other: { hours: "2", start_time: "10:00" }
  };

  useEffect(() => {
    fetchHours();
    fetchPendingVolunteers();
    fetchMyHours();
  }, []);

  const fetchPendingVolunteers = async () => {
    try {
      const response = await axios.get(`${API}/pending-volunteers`);
      setPendingVolunteers(response.data);
    } catch (error) {
      console.error("Failed to fetch pending volunteers", error);
    }
  };

  const fetchMyHours = async () => {
    try {
      const response = await axios.get(`${API}/supervisor/my-hours`);
      setMyHours(response.data);
    } catch (error) {
      console.error("Failed to fetch my hours", error);
    }
  };

  const fetchHours = async () => {
    try {
      const response = await axios.get(`${API}/hours`);
      setHours(response.data.sort((a, b) => {
        // Pending first, then by date
        if (a.status === 'pending' && b.status !== 'pending') return -1;
        if (a.status !== 'pending' && b.status === 'pending') return 1;
        return new Date(b.created_at) - new Date(a.created_at);
      }));
    } catch (error) {
      toast.error("Failed to fetch hours");
    }
  };

  const getMinDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 10);
    return date.toISOString().split('T')[0];
  };

  const handleActivityChange = (value) => {
    const defaults = activityDefaults[value] || {};
    setFormData({
      ...formData,
      activity_type: value,
      hours: defaults.hours || formData.hours,
      start_time: defaults.start_time || formData.start_time
    });
  };

  const handleSubmitHours = async (e) => {
    e.preventDefault();

    if (!formData.activity_type) {
      toast.error("Please select an activity type");
      return;
    }

    const selectedDate = new Date(formData.date);
    const minDate = new Date(getMinDate());
    const today = new Date();
    
    if (selectedDate < minDate) {
      toast.error("You can only log hours from the last 10 days");
      return;
    }
    
    if (selectedDate > today) {
      toast.error("You cannot log hours for future dates");
      return;
    }

    // Show confirmation modal
    setShowLogHoursDialog(false);
    setShowConfirmationModal(true);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirmationModal(false);
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/supervisor/log-my-hours`, {
        date: formData.date,
        start_time: formData.start_time,
        hours: parseFloat(formData.hours),
        activity_type: formData.activity_type,
        description: formData.description
      });
      
      toast.success("Hours logged and auto-approved!");
      setShowLogHoursDialog(false);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        start_time: "09:00",
        hours: "",
        activity_type: "",
        description: ""
      });
      fetchMyHours();
      fetchHours();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to log hours");
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmCancel = () => {
    setShowConfirmationModal(false);
    setShowLogHoursDialog(true);
  };

  const formatDateForDisplay = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTimeForDisplay = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
  };

  const handleApproval = async (hourId, status) => {
    setLoading({ ...loading, [hourId]: true });
    try {
      await axios.put(`${API}/hours/${hourId}/approve`, { status });
      toast.success(`Hours ${status}!`);
      fetchHours();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally {
      setLoading({ ...loading, [hourId]: false });
    }
  };

  const handleVolunteerApproval = async (volunteerId, status) => {
    setLoading({ ...loading, [`vol_${volunteerId}`]: true });
    try {
      await axios.put(`${API}/approve-volunteer/${volunteerId}`, { status });
      toast.success(`Volunteer account ${status}!`);
      fetchPendingVolunteers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update volunteer status");
    } finally {
      setLoading({ ...loading, [`vol_${volunteerId}`]: false });
    }
  };

  const getStatusBadge = (status) => {
    if (status === "club_approved" || status === "platform_approved" || status === "approved") {
      return <Badge className="bg-green-100 text-green-700 border-green-300"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
    } else if (status === "rejected") {
      return <Badge className="bg-red-100 text-red-700 border-red-300"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
    }
    return <Badge className="bg-amber-100 text-amber-700 border-amber-300"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
  };

  const pendingHours = hours.filter(h => h.status === 'pending');
  const reviewedHours = hours.filter(h => h.status !== 'pending');

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-red-50">
      {/* Header */}
      <header className="bg-white border-b border-amber-100 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_volunteer-hub-35/artifacts/5ta1s578_playback%20logo.jpg" 
                alt="Logo" 
                className="w-16 h-16"
              />
              <div>
                <h1 className="text-4xl font-black text-red-600 brand-font leading-none">PlayBack Token</h1>
                <p className="text-sm text-gray-600 mt-1">{user.club_name} - Supervisor</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-700">{user.first_name} {user.last_name}</p>
                <p className="text-xs text-gray-500 capitalize">{user.role}</p>
              </div>
              <Button variant="outline" onClick={logout} data-testid="logout-button" className="border-amber-300 text-amber-600 hover:bg-amber-50">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs for Approvals and Analytics */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="overflow-x-auto mb-6">
            <TabsList className="grid w-full grid-cols-6 min-w-max sm:min-w-0 h-auto">
              <TabsTrigger value="myhours" data-testid="tab-myhours" className="text-xs sm:text-base py-3 px-2 sm:px-4">
                <Plus className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Log My Hours</span>
                <span className="sm:hidden">My Hours</span>
              </TabsTrigger>
              {pendingVolunteers.length > 0 && (
                <TabsTrigger value="accounts" data-testid="tab-accounts" className="text-xs sm:text-base relative py-3 px-2 sm:px-4">
                  <Users className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Pending Accounts</span>
                  <span className="sm:hidden">Accounts</span>
                  <span className="ml-1 sm:ml-2 bg-red-600 text-white text-xs px-1.5 sm:px-2 py-0.5 rounded-full">
                    {pendingVolunteers.length}
                  </span>
                </TabsTrigger>
              )}
              <TabsTrigger value="approvals" data-testid="tab-approvals" className="text-xs sm:text-base py-3 px-2 sm:px-4">
                <CheckCircle className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Hours Approval</span>
                <span className="sm:hidden">Approvals</span>
              </TabsTrigger>
              <TabsTrigger value="stripe" data-testid="tab-stripe" className="text-xs sm:text-base py-3 px-2 sm:px-4">
                <CreditCard className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Stripe Payments</span>
                <span className="sm:hidden">Stripe</span>
              </TabsTrigger>
              <TabsTrigger value="earnings" data-testid="tab-earnings" className="text-xs sm:text-base py-3 px-2 sm:px-4">
                <DollarSign className="w-4 h-4 mr-1 sm:mr-2" />
                Earnings
              </TabsTrigger>
              <TabsTrigger value="analytics" data-testid="tab-analytics" className="text-xs sm:text-base py-3 px-2 sm:px-4">
                <BarChart3 className="w-4 h-4 mr-1 sm:mr-2" />
                Analytics
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="myhours" className="space-y-6">
            {/* Log My Hours Section */}
            <Card className="shadow-lg border-amber-100">
              <CardHeader>
                <CardTitle className="text-2xl text-amber-600">Log My Volunteer Hours</CardTitle>
                <CardDescription>As a supervisor, your hours are automatically club-approved</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center gap-6">
                  <Button
                    onClick={() => setShowLogHoursDialog(true)}
                    className="bg-amber-600 hover:bg-amber-700 text-white text-lg py-6 px-8"
                    data-testid="log-my-hours-btn"
                  >
                    <Plus className="w-6 h-6 mr-2" />
                    Log My Volunteer Hours
                  </Button>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 w-full max-w-md text-center">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> Your hours will be automatically approved at club level 
                      and marked as self-logged. They still require platform approval before tokens can be sold.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* My Hours History */}
            <Card className="shadow-lg border-amber-100">
              <CardHeader>
                <CardTitle className="text-xl text-gray-800">My Logged Hours</CardTitle>
                <CardDescription>Hours you&apos;ve logged as a supervisor</CardDescription>
              </CardHeader>
              <CardContent>
                {myHours.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">No hours logged yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {myHours.map((hour) => (
                      <div 
                        key={hour.id}
                        className="border rounded-lg p-4 bg-white hover:shadow-md transition-shadow"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold capitalize">{hour.activity_type}</span>
                              <Badge className={
                                hour.status === 'platform_approved' ? "bg-green-100 text-green-700" :
                                hour.status === 'club_approved' ? "bg-blue-100 text-blue-700" :
                                hour.status === 'rejected' ? "bg-red-100 text-red-700" :
                                "bg-yellow-100 text-yellow-700"
                              }>
                                {hour.status === 'club_approved' ? 'Awaiting Platform Approval' : 
                                 hour.status === 'platform_approved' ? 'Fully Approved' :
                                 hour.status}
                              </Badge>
                              {hour.self_logged && (
                                <Badge className="bg-amber-100 text-amber-700 text-xs">
                                  Self-logged
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">{hour.description}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {new Date(hour.date).toLocaleDateString()} at {hour.start_time}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="text-2xl font-bold text-amber-600">{hour.hours}h</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="accounts" className="space-y-8">
            {/* Pending Volunteer Accounts */}
            <Card className="shadow-lg border-red-100">
              <CardHeader>
                <CardTitle className="text-2xl text-red-600">Pending Volunteer Accounts</CardTitle>
                <CardDescription>Review and approve new volunteer signups for your club</CardDescription>
              </CardHeader>
              <CardContent>
                {pendingVolunteers.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">No pending volunteer accounts</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingVolunteers.map((volunteer) => (
                      <div 
                        key={volunteer.id} 
                        data-testid={`pending-volunteer-${volunteer.id}`} 
                        className="border-2 border-red-200 rounded-lg p-4 bg-red-50 hover:shadow-md transition-shadow"
                      >
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold text-gray-800">{volunteer.first_name} {volunteer.last_name}</h3>
                              <Badge className="bg-orange-100 text-orange-700 border-orange-300">
                                <Clock className="w-3 h-3 mr-1" />
                                Pending Approval
                              </Badge>
                            </div>
                            <div className="text-sm text-gray-600">
                              <p><span className="font-medium">Email:</span> <span className="font-mono text-blue-600">{volunteer.email}</span></p>
                              <p><span className="font-medium">Club:</span> {volunteer.club_name}</p>
                              <p><span className="font-medium">Signed up:</span> {new Date(volunteer.created_at).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <div className="flex flex-col sm:flex-row gap-2 ml-0 sm:ml-4 w-full sm:w-auto mt-3 sm:mt-0">
                            <Button
                              onClick={() => handleVolunteerApproval(volunteer.id, 'approved')}
                              data-testid={`approve-volunteer-${volunteer.id}`}
                              disabled={loading[`vol_${volunteer.id}`]}
                              className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto h-11"
                              size="sm"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve Account
                            </Button>
                            <Button
                              onClick={() => handleVolunteerApproval(volunteer.id, 'rejected')}
                              data-testid={`reject-volunteer-${volunteer.id}`}
                              disabled={loading[`vol_${volunteer.id}`]}
                              variant="outline"
                              className="border-red-300 text-red-600 hover:bg-red-50 w-full sm:w-auto h-11"
                              size="sm"
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="approvals" className="space-y-8">
            {/* Nested Tabs for Hours */}
            <Tabs defaultValue="pending" className="w-full">
              <div className="overflow-x-auto mb-6">
                <TabsList className="grid w-full grid-cols-4 min-w-max sm:min-w-0 h-auto">
                  <TabsTrigger value="pending" className="text-xs sm:text-sm py-3 px-2 sm:px-4">
                    <Clock className="w-4 h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Pending Approvals</span>
                    <span className="sm:hidden">Pending</span>
                  </TabsTrigger>
                  <TabsTrigger value="approved" className="text-xs sm:text-sm py-3 px-2 sm:px-4">
                    <CheckCircle className="w-4 h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Hours I Approved</span>
                    <span className="sm:hidden">Approved</span>
                  </TabsTrigger>
                  <TabsTrigger value="rejected" className="text-xs sm:text-sm py-3 px-2 sm:px-4">
                    <XCircle className="w-4 h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Hours I Rejected</span>
                    <span className="sm:hidden">Rejected</span>
                  </TabsTrigger>
                  <TabsTrigger value="pbt-reviewed" className="text-xs sm:text-sm py-3 px-2 sm:px-4">
                    <BarChart3 className="w-4 h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">PBT Reviewed</span>
                    <span className="sm:hidden">PBT</span>
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Pending Approvals Tab */}
              <TabsContent value="pending">
                <Card className="shadow-lg border-amber-100">
          <CardHeader>
            <CardTitle className="text-2xl text-amber-600">Pending Approvals</CardTitle>
            <CardDescription>Review and approve volunteer hours for your club</CardDescription>
          </CardHeader>
          <CardContent>
            {pendingHours.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No pending approvals. All caught up!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingHours.map((hour) => (
                  <div key={hour.id} data-testid={`pending-hour-${hour.id}`} className="border-2 border-amber-200 rounded-lg p-4 bg-amber-50 hover:shadow-md transition-shadow">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">{hour.volunteer_name}</h3>
                          {getStatusBadge(hour.status)}
                        </div>
                        {hour.description && (
                          <p className="text-sm text-gray-700 mb-2 italic bg-white px-3 py-2 rounded border border-amber-200">"{hour.description}"</p>
                        )}
                        {hour.hours_explanation && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mb-2">
                            <p className="text-xs font-semibold text-yellow-800 mb-1">⚠️ High Hours Explanation:</p>
                            <p className="text-sm text-yellow-900 italic">"{hour.hours_explanation}"</p>
                          </div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-gray-700">
                          <p><span className="font-medium">Activity:</span> <span className="capitalize">{hour.activity_type}</span></p>
                          <p><span className="font-medium">Date:</span> {new Date(hour.date).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: '2-digit' })}</p>
                          <p><span className="font-medium">Start Time:</span> {hour.start_time}</p>
                          <p><span className="font-medium">Hours:</span> {hour.hours}</p>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto sm:ml-4">
                        <Button
                          onClick={() => handleApproval(hour.id, 'approved')}
                          data-testid={`approve-button-${hour.id}`}
                          disabled={loading[hour.id]}
                          className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto h-11"
                          size="sm"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          onClick={() => handleApproval(hour.id, 'rejected')}
                          data-testid={`reject-button-${hour.id}`}
                          disabled={loading[hour.id]}
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50 w-full sm:w-auto h-11"
                          size="sm"
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
                </Card>
              </TabsContent>

              {/* Hours I Approved Tab */}
              <TabsContent value="approved">
                <Card className="shadow-lg border-green-100">
                  <CardHeader>
                    <CardTitle className="text-2xl text-green-600">Hours I Approved</CardTitle>
                    <CardDescription>Volunteer hours you approved - awaiting PBT review</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {hours.filter(h => h.status === 'club_approved' && h.supervisor_id === user.id).length === 0 ? (
                      <div className="text-center py-12">
                        <CheckCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500">No approved hours yet.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {hours.filter(h => h.status === 'club_approved' && h.supervisor_id === user.id).map((hour) => (
                          <div key={hour.id} className="border border-green-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h3 className="font-semibold text-lg">{hour.volunteer_name}</h3>
                                  <Badge className="bg-green-100 text-green-700 border-green-300">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Awaiting PBT Review
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-4 mb-2 text-sm text-gray-700">
                                  <p>{new Date(hour.date).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: '2-digit' })}</p>
                                  <p className="text-gray-500">•</p>
                                  <p className="font-semibold capitalize">{hour.activity_type}</p>
                                  <p className="text-gray-500">•</p>
                                  <p>{hour.start_time}</p>
                                  <p className="text-gray-500">•</p>
                                  <p className="font-medium">{hour.hours} hours</p>
                                </div>
                                {hour.description && (
                                  <p className="text-sm text-gray-700 mb-2 italic bg-gray-50 p-2 rounded">"{hour.description}"</p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold text-green-600">{hour.hours}h</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Hours I Rejected Tab */}
              <TabsContent value="rejected">
                <Card className="shadow-lg border-red-100">
                  <CardHeader>
                    <CardTitle className="text-2xl text-red-600">Hours I Rejected</CardTitle>
                    <CardDescription>Volunteer hours you rejected</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {hours.filter(h => h.status === 'rejected' && h.supervisor_id === user.id).length === 0 ? (
                      <div className="text-center py-12">
                        <XCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500">No rejected hours.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {hours.filter(h => h.status === 'rejected' && h.supervisor_id === user.id).map((hour) => (
                          <div key={hour.id} className="border border-red-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-red-50">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h3 className="font-semibold text-lg">{hour.volunteer_name}</h3>
                                  <Badge className="bg-red-100 text-red-700 border-red-300">
                                    <XCircle className="w-3 h-3 mr-1" />
                                    Rejected
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-4 mb-2 text-sm text-gray-700">
                                  <p>{new Date(hour.date).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: '2-digit' })}</p>
                                  <p className="text-gray-500">•</p>
                                  <p className="font-semibold capitalize">{hour.activity_type}</p>
                                  <p className="text-gray-500">•</p>
                                  <p>{hour.start_time}</p>
                                  <p className="text-gray-500">•</p>
                                  <p className="font-medium">{hour.hours} hours</p>
                                </div>
                                {hour.description && (
                                  <p className="text-sm text-gray-700 mb-2 italic bg-white p-2 rounded">"{hour.description}"</p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold text-red-600">{hour.hours}h</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* PBT Reviewed Tab */}
              <TabsContent value="pbt-reviewed">
                <Card className="shadow-lg border-purple-100">
                  <CardHeader>
                    <CardTitle className="text-2xl text-purple-600">PBT Reviewed Hours</CardTitle>
                    <CardDescription>Hours you approved that PBT has reviewed</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {hours.filter(h => (h.status === 'platform_approved' || (h.status === 'rejected' && h.rejected_by === 'admin')) && h.supervisor_id === user.id).length === 0 ? (
                      <div className="text-center py-12">
                        <BarChart3 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500">No PBT reviewed hours yet.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {hours.filter(h => (h.status === 'platform_approved' || (h.status === 'rejected' && h.rejected_by === 'admin')) && h.supervisor_id === user.id).map((hour) => (
                          <div key={hour.id} className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${hour.status === 'platform_approved' ? 'border-purple-200 bg-purple-50' : 'border-red-200 bg-red-50'}`}>
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <h3 className="font-semibold text-lg">{hour.volunteer_name}</h3>
                                  {hour.status === 'platform_approved' ? (
                                    <Badge className="bg-purple-100 text-purple-700 border-purple-300">
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      PBT Approved
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-red-100 text-red-700 border-red-300">
                                      <XCircle className="w-3 h-3 mr-1" />
                                      PBT Rejected
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-4 mb-2 text-sm text-gray-700">
                                  <p>{new Date(hour.date).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: '2-digit' })}</p>
                                  <p className="text-gray-500">•</p>
                                  <p className="font-semibold capitalize">{hour.activity_type}</p>
                                  <p className="text-gray-500">•</p>
                                  <p>{hour.start_time}</p>
                                  <p className="text-gray-500">•</p>
                                  <p className="font-medium">{hour.hours} hours</p>
                                </div>
                                {hour.description && (
                                  <p className="text-sm text-gray-700 mb-2 italic bg-white p-2 rounded">"{hour.description}"</p>
                                )}
                                {hour.rejection_reason && (
                                  <div className="bg-red-50 border border-red-200 rounded p-2 mt-2">
                                    <p className="text-xs font-semibold text-red-800 mb-1">Rejection Reason:</p>
                                    <p className="text-sm text-red-900">"{hour.rejection_reason}"</p>
                                  </div>
                                )}
                              </div>
                              <div className="text-right">
                                <p className={`text-2xl font-bold ${hour.status === 'platform_approved' ? 'text-purple-600' : 'text-red-600'}`}>{hour.hours}h</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="stripe">
            <StripeConnectTab />
          </TabsContent>

          <TabsContent value="earnings">
            <SupervisorEarningsTab />
          </TabsContent>

          <TabsContent value="analytics">
            <ClubAnalytics user={user} />
          </TabsContent>
        </Tabs>
      </main>

      {/* Log Hours Dialog */}
      {showLogHoursDialog && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" 
          style={{ zIndex: 99999 }}
        >
          <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-800">Log My Volunteer Hours</h3>
              <button 
                onClick={() => setShowLogHoursDialog(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmitHours} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="date">Date *</Label>
                  <Input
                    id="date"
                    type="date"
                    required
                    min={getMinDate()}
                    max={new Date().toISOString().split('T')[0]}
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="start_time">Start Time *</Label>
                  <Input
                    id="start_time"
                    type="time"
                    required
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Activity Type *</Label>
                  <Select value={formData.activity_type} onValueChange={handleActivityChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select activity" />
                    </SelectTrigger>
                    <SelectContent style={{ zIndex: 999999 }}>
                      {activityTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="hours">Hours *</Label>
                  <Input
                    id="hours"
                    type="number"
                    required
                    min="0.5"
                    max="24"
                    step="0.5"
                    value={formData.hours}
                    onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                    placeholder="e.g., 2"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe what you did..."
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowLogHoursDialog(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Hours"
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Confirmation Modal */}
      {showConfirmationModal && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" 
          style={{ zIndex: 99999 }}
        >
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Confirm Your Hours</h3>
              <p className="text-gray-600 text-sm">These hours will be automatically club-approved</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-3">
              <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                <span className="text-sm text-gray-600">Date</span>
                <span className="font-semibold text-gray-800">{formatDateForDisplay(formData.date)}</span>
              </div>
              <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                <span className="text-sm text-gray-600">Activity Type</span>
                <span className="font-semibold text-gray-800 capitalize">{formData.activity_type}</span>
              </div>
              <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                <span className="text-sm text-gray-600">Start Time</span>
                <span className="font-semibold text-gray-800">{formatTimeForDisplay(formData.start_time)}</span>
              </div>
              <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                <span className="text-sm text-gray-600">Hours</span>
                <span className="font-bold text-amber-600 text-lg">{formData.hours}h</span>
              </div>
              <div className="pt-2">
                <span className="text-sm text-gray-600 block mb-1">Description</span>
                <p className="text-gray-800 bg-white p-2 rounded border border-gray-200 text-sm">{formData.description}</p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleConfirmCancel}
                className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-lg transition-colors"
              >
                Edit
              </button>
              <button
                onClick={handleConfirmSubmit}
                disabled={submitting}
                className="flex-1 px-4 py-3 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Confirm & Submit
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SupervisorDashboard;
